import { useLocation } from "react-router-dom";

/** `/data-connectors/:connectionId/tables/:schema/:table` — full-width table view */
const CONNECTOR_DATASET_TABLE_PATH =
  /^\/data-connectors\/[^/]+\/tables\/[^/]+\/[^/]+$/;

/**
 * Old routes: `/story/detail/:storyId` and `/story/generate/:storyId`
 * New routes: `/workspace/:workspaceId/story/:storyId/plan|build|present|explore`
 * All are full-bleed three-column layouts (chat | builder/canvas | utility rail)
 * and share the same shell treatment: padding:0, overflow:hidden, 0% margins.
 * Personalized distribution pages (`…/story/:storyId/distributions…`) keep the
 * story chrome too: the header shows the story and its Share menu; the pages
 * scroll inside their own box.
 */
export const STORY_DETAIL_PATH =
  /^\/story\/(detail|generate)\/[^/]+$|^\/workspace\/[^/]+\/(story\/[^/]+\/(plan\/[^/]+|build|present|explore|distributions(?:\/[^?#]*)?)|csv-loading)$/;

export const useGlobalPath = (): {
  isGlobalPath: boolean;
  isStoryPaths: boolean;
  isLoginPath: boolean;
  isHomePath: boolean;
  isConnectorDetailPath: boolean;
  /** Connectors marketplace list only (`/data-connectors`, not child routes). */
  isConnectorMarketplacePath: boolean;
  /** True on the dataset preview route (table drill-down). */
  isConnectorDatasetTablePath: boolean;
  /** Instance sidebar (connector dropdown + list); hidden on dataset table route. */
  showConnectorInstanceSidebar: boolean;
  /** Any route under `/knowledge-graph`. */
  isKnowledgeGraphPath: boolean;
  /** Any route under `/workspaces`. */
  isWorkspacesPath: boolean;
  /** Agents catalog and future child routes. */
  isAgentsPath: boolean;
  /** Story detail/generate pages (`/story/detail|generate/:storyId`) — full-bleed, no primary nav. */
  isStoryDetailPath: boolean;
} => {
  const location = useLocation();
  const globalPaths = [
    "/home",
    "/stories",
    "/data-connectors",
    "/automation",
    "/permissions",
    "/activity",
    "/templates",
    "/discover",
    "/settings",
    "/workspaces",
  ];
  const storyPathRegexes = [
    /^\/explore\/[^/]+\/[^/]+$/,
    /^\/build\/[^/]+\/[^/]+$/,
    /^\/present\/[^/]+\/[^/]+$/,
  ];
  const isConnectorDetailPath =
    location.pathname.startsWith("/data-connectors/") &&
    location.pathname !== "/data-connectors";
  const isConnectorDatasetTablePath = CONNECTOR_DATASET_TABLE_PATH.test(
    location.pathname,
  );
  const showConnectorInstanceSidebar =
    isConnectorDetailPath && !isConnectorDatasetTablePath;
  const isConnectorMarketplacePath = location.pathname === "/data-connectors";
  const isKnowledgeGraphPath = location.pathname.startsWith("/knowledge-graph");
  const isWorkspacesPath = location.pathname.startsWith("/workspaces");
  const isStoryDetailPath = STORY_DETAIL_PATH.test(location.pathname);
  const isAgentsPath = /^\/agents(?:\/|$)/.test(location.pathname);
  return {
    isGlobalPath:
      !isConnectorDetailPath &&
      (isAgentsPath ||
        globalPaths.some((path) => location.pathname.startsWith(path))),
    isStoryPaths: storyPathRegexes.some((regex) =>
      regex.test(location.pathname),
    ),
    isLoginPath: location.pathname === "/login" || location.pathname === "/",
    isHomePath:
      location.pathname === "/home" || location.pathname === "/home-old",
    isConnectorDetailPath,
    isConnectorMarketplacePath,
    isConnectorDatasetTablePath,
    showConnectorInstanceSidebar,
    isKnowledgeGraphPath,
    isWorkspacesPath,
    isAgentsPath,
    isStoryDetailPath,
  };
};
